/*
 * Steel Pony Farm — fundraiser attribution (2026-08-30).
 *
 * A group shares a link: shop.steelpony.ca/?fundraiser=mountview-school
 * We capture the slug, remember it, and write it to the cart as the "Fundraiser"
 * attribute so it rides through to the order — the same path "Pickup Location"
 * already uses. The console reads that attribute to total the group's sales and
 * compute the payout.
 *
 * DELIBERATELY A SEPARATE FILE from spf-fulfillment.js. That script is the ONLY way a
 * customer can choose pickup or delivery; if it throws, they are stranded at "Choose an
 * option above" with no way out (see the js-only checkout lockout, 2026-08-03). Nothing
 * about a fundraiser is worth sharing that blast radius, so this file fails silently and
 * alone.
 *
 * THE FAILURE THAT MATTERS is silent non-attribution: the family orders happily, gets
 * their food, and the group is simply short at payout time with no way to reconstruct
 * who should have counted. So:
 *   - the slug is persisted, not just held in the cart (carts empty and expire);
 *   - it is re-applied on every page load where the cart lacks it;
 *   - it expires after 60 days so an October participant is not still credited to that
 *     fundraiser in December;
 *   - the storefront renders a visible banner when it is set, which is both a trust
 *     signal and the error detector — if it is missing, the customer says so.
 *
 * THE MARKER (added 2026-08-31, task-fundraiser-program.md §5c). Attribution is only half
 * the job: a fundraiser cart must also carry a $0 marker product, because that is the only
 * thing the checkout rule can actually SEE — it can test cart CONTENTS or a customer TAG,
 * and a first-time buyer has neither an account nor a tag. Without it a parent buying $32
 * of vegetables hits the $50 minimum with no explanation, which is precisely the person
 * this program exists to reach.
 *
 * The share link adds it server-side (/cart/add?id=…&return_to=/?fundraiser=slug) so it
 * survives this file throwing. What that CANNOT cover is the returning shopper: they order
 * on the Friday, the cart empties, they come back on the Sunday inside the same window with
 * the slug still stored — attribution re-applies, the marker does not, and the exemption is
 * silently gone. So the marker is re-ensured here on the same terms as the attribute.
 * Never onto a cart holding rescue items: those carry their own exemption and a foreign
 * line would reclassify the cart as "mixed" and block checkout outright.
 *
 * Cross-device is the normal case, not the edge case: a parent opens the link on their
 * phone from a class group chat and shops on a laptop that evening. localStorage does not
 * follow them. That is a KNOWN, UNSOLVED gap — see task-fundraiser-program.md.
 */
(function () {
  'use strict';

  var KEY = 'spf_fundraiser';
  var ATTR = 'Fundraiser';
  var TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60 days

  // Every storage access is wrapped: private mode and blocked-site-data both throw on
  // access rather than returning null.
  function readStored() {
    try {
      var raw = window.localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  // early = the slug was remembered from a link tapped BEFORE its group's ordering week
  // (2026-10-25, see REPEAT below). A link tapped in the week overwrites it without the flag.
  function writeStored(slug, early) {
    try {
      var o = { slug: slug, exp: Date.now() + TTL_MS };
      if (early) o.early = true;
      window.localStorage.setItem(KEY, JSON.stringify(o));
    } catch (e) {
      /* no storage — the cart attribute alone still carries this session */
    }
  }
  function storedEarly() {
    var s = readStored();
    return !!(s && s.early);
  }

  // EARLY CLICKS DO NOT CREDIT EXISTING CUSTOMERS (Mike 2026-10-25). Mackenzie, a regular with
  // no tie to Eastview, opened the store from the Sunday email and her cart was credited to
  // Eastview: her phone had tapped an Eastview link weeks before it opened, and the early-link
  // memory (2026-09-28) turned that into a tag on opening day. Mike: "not credit early clickers
  // if they are pre-existing customers." So a slug remembered from an EARLY tap credits only a
  // shopper who has not ordered before. Signed-in repeat customers are known here and are never
  // tagged from an early tap; everyone else is tagged with VIA = 'early-link' and the payout
  // (fundraiser/closeout.js) leaves the order out if the customer had ordered before the
  // group opened. Tapping the link again in the fundraiser's week counts as normal.
  var VIA = 'Fundraiser via';
  function isRepeat() {
    var el = document.querySelector('[data-spf-fr-labels]');
    return !!el && el.getAttribute('data-spf-fr-repeat') === '1';
  }

  function clearStored() {
    try {
      window.localStorage.removeItem(KEY);
    } catch (e) {}
  }

  function storedSlug() {
    var s = readStored();
    if (!s || !s.slug) return null;
    if (!s.exp || Date.now() > s.exp) {
      clearStored();
      return null;
    }
    return s.slug;
  }

  // Slugs are kebab-case and bounded. Anything else is somebody poking at the URL, and a
  // junk value would only pollute order attributes and the console's totals.
  function cleanSlug(raw) {
    var s = String(raw || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    return s.length >= 2 && s.length <= 64 ? s : null;
  }

  function paramSlug() {
    try {
      return resolveSlug(cleanSlug(new URLSearchParams(window.location.search).get('fundraiser')));
    } catch (e) {
      return null;
    }
  }

  // A SHORTENED OR MISTYPED LINK STILL FINDS ITS GROUP (Mike 2026-10-02). On Mountview's opening
  // day two visits arrived as ?fundraiser=mountview - the /f/* redirect passes whatever was
  // typed after /f/, and someone dropped the "-school". Those orders would have carried a tag
  // no group owns and counted for nobody. So a slug that is not a published group is matched
  // to the ONE published group it is the start of (hyphens ignored, at least 4 letters), or
  // that is the start of it. Two candidates = no guess; the slug is left as it was.
  function resolveSlug(s) {
    if (!s) return s;
    var groups = knownGroups();
    if (!groups || groups[s]) return s;
    var flat = s.replace(/-/g, '');
    if (flat.length < 4) return s;
    var hit = null;
    for (var k in groups) {
      if (!Object.prototype.hasOwnProperty.call(groups, k)) continue;
      var kf = k.replace(/-/g, '');
      if (kf.indexOf(flat) === 0 || flat.indexOf(kf) === 0) {
        if (hit) return s;              // ambiguous - never guess between two groups
        hit = k;
      }
    }
    return hit || s;
  }

  // The cart gate, the drawer and the bubble all re-read the cart on `cart:update`
  // (spf-fulfillment.js). Our writes go through plain fetch, which fires nothing — so the
  // page kept the state it rendered with. Live on 2026-09-22: a fundraiser cart built from a
  // share link sat at "You're $30.50 short of the $50 minimum" with the Check out button
  // DISABLED, because the marker line arrived after the gate had already decided. One event
  // fixes all of them.
  function announceCart() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        ['cart:update', 'cart:refresh', 'cart:updated', 'theme:cart:update'].forEach(function (n) {
          document.dispatchEvent(new CustomEvent(n, { detail: { resource: cart, cart: cart }, bubbles: true }));
        });
      })
      .catch(function () { /* silent */ });
  }

  function applyToCart(slug, fromLink) {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        if (!cart) return null;
        var current = (cart.attributes && cart.attributes[ATTR]) || '';
        var curVia = (cart.attributes && cart.attributes[VIA]) || '';
        var via = fromLink ? 'link' : (storedEarly() ? 'early-link' : 'remembered');
        // Already right: don't churn the cart. The one write worth making on a cart already
        // tagged for this group is upgrading an early-link tag once the link is tapped again.
        if (current === slug && !(curVia === 'early-link' && via === 'link')) return null;

        // A slug arriving in the URL is an explicit choice and always wins. A slug coming
        // only from storage must NOT quietly overwrite a different one already on the
        // cart — that one got there by its own explicit link.
        if (!fromLink && current && current !== slug) return null;

        var attributes = {};
        attributes[ATTR] = slug;
        attributes[VIA] = curVia === 'link' && current === slug ? 'link' : via;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes }),
        });
      })
      .then(function (res) {
        if (res && res.ok) {
          document.dispatchEvent(new CustomEvent('spf:fundraiser', { detail: { slug: slug } }));
          announceCart();
        }
      })
      .catch(function () {
        /* silent — never block shopping over attribution */
      });
  }

  // The marker product's handle, published by spf-fulfillment-global.liquid from the ONE
  // theme setting that the cart rows, the badge and the $50 gate also read. Blank = the
  // whole marker mechanism is off, and everything below becomes a no-op.
  function markerHandle() {
    var el = document.querySelector('[data-spf-fundraiser-marker]');
    var h = el && el.getAttribute('data-spf-fundraiser-marker');
    h = h ? h.trim() : '';
    return h || null;
  }

  // Rescue/flash carts are off limits — see the header note. The flags are rendered
  // server-side by cart-summary.liquid because /cart.js carries no product tags.
  function cartHasFlash() {
    var el = document.querySelector('[data-spf-flash-state]');
    return !!el && parseInt(el.getAttribute('data-flash') || '0', 10) > 0;
  }

  // One marker, one at a time. Without the in-flight guard the cart:update we now fire
  // after our own writes re-enters this before the first add has landed, and the cart ends
  // up with the marker TWICE (seen live 2026-09-22, quantity 2).
  var markerBusy = false;

  function ensureMarker() {
    var handle = markerHandle();
    if (!handle || cartHasFlash() || markerBusy) return;
    markerBusy = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return null;
        var items = cart.items || [];
        // An empty cart is an arrival, not an order: adding the marker there would put a
        // "1" on the badge over a cart that shows nothing, and make a $0 order possible.
        if (!items.length) return null;
        for (var i = 0; i < items.length; i++) {
          if (items[i].handle !== handle) continue;
          if (items[i].quantity > 1) {
            // Trim a doubled marker back to one rather than leaving it on the order.
            var fix = {};
            fix[items[i].key] = 1;
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates: fix }),
            });
          }
          return null; // already carried, exactly once
        }
        return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (product) {
            var v = product && product.variants && product.variants[0];
            if (!v || !v.id) return null; // unpublished / renamed handle — stay silent
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: v.id, quantity: 1 }),
            }).then(function (res) {
              // The marker IS the $50 exemption as far as the cart gate is concerned.
              if (res && res.ok) announceCart();
              return res;
            });
          });
      })
      .catch(function () {
        /* silent — never block shopping over the exemption */
      })
      .then(function () { markerBusy = false; }, function () { markerBusy = false; });
  }

  // FIRST-PAGEVIEW BAR (2026-09-22). The banner is server-rendered from the cart attribute,
  // which does not exist yet on the pageview a share link lands on — so the family arrived
  // with no confirmation at all until they happened to load a second page. This paints the
  // same markup from the slug we already have, using the label list the snippet renders.
  // Silent no-op if the bar is already there, if the list is missing, or if the slug is not
  // in it (an invented slug must never claim a group's name).
  // The groups the storefront knows about, with each one's shopping window. Rendered by
  // snippets/spf-fundraiser-banner.liquid from the theme's own list plus the console's
  // (shop metafield spf.fundraisers). Returns undefined when the list itself is absent —
  // an old theme — which is NOT the same as "this group is unknown", see expiry below.
  function knownGroups() {
    var el = document.querySelector('[data-spf-fr-labels]');
    if (!el) return undefined;
    var out = {};
    var raw = el.getAttribute('data-spf-fr-labels') || '';
    var pairs = raw ? raw.split('|') : [];
    for (var i = 0; i < pairs.length; i++) {
      var b = pairs[i].split('^^^');
      var slug = (b[0] || '').trim();
      if (slug) {
        out[slug] = {
          label: (b[1] || '').trim(), from: (b[2] || '').trim(), until: (b[3] || '').trim(),
          families: parseInt((b[4] || '0').trim(), 10) || 0,
        };
      }
    }
    return out;
  }

  // The tag the cart carried when the server rendered this page, from the same element.
  function cartSlug() {
    var el = document.querySelector('[data-spf-fr-labels]');
    return el ? cleanSlug(el.getAttribute('data-spf-fr-cart')) : null;
  }

  function labelFor(slug) {
    var g = knownGroups();
    return g && g[slug] && g[slug].label ? g[slug].label : null;
  }

  // Families so far, or 0. Same threshold as the snippet: a small number reads worse than
  // no number at all, so it stays hidden until there are enough of them.
  var COUNT_MIN = 7;
  function familiesFor(slug) {
    var g = knownGroups();
    return g && g[slug] ? g[slug].families || 0 : 0;
  }

  // "2026-10-05T23:59" / "2026-10-05" -> a comparable number in the shop's own wall clock,
  // matched against the same reading of the shopper's clock. Minutes-level drift between
  // the two clocks is irrelevant here: this decides whether a window ENDED, and the answer
  // only changes hours after the last order of that fundraiser can be placed.
  function stamp(s) {
    var d = String(s || '').replace(/[-T:]/g, '');
    if (d.length === 8) d += '2359';
    return d.length === 12 ? parseInt(d, 10) : null;
  }
  function nowStamp() {
    var d = new Date();
    var p = function (n) { return (n < 10 ? '0' : '') + n; };
    return parseInt('' + d.getFullYear() + p(d.getMonth() + 1) + p(d.getDate()) + p(d.getHours()) + p(d.getMinutes()), 10);
  }

  // THE HARD RULE (Mike, 2026-09-22): "we can't under any circumstances have families' next
  // order come into the fundraiser total ... it has to be built in."
  //
  // The tag used to sit in storage for 60 days, so a family who shopped again in November
  // still arrived attributed. The payout screen would have shown those orders as
  // after-window and left them out of the total, but the tag was still riding on their
  // orders, and one tick would have paid the school for a stranger's groceries.
  //
  // So the tag now dies with the SHOPPING WINDOW, not on a timer, and it dies in the
  // shopper's browser rather than being cleaned up later: over → clear storage, clear the
  // cart attribute, drop the $0 marker line. A slug the storefront no longer lists is
  // treated as over too — that is what a finished fundraiser looks like once the console
  // stops publishing it. Unknown-because-the-list-is-missing is the one case that changes
  // nothing (an old theme must not wipe a live fundraiser).
  function windowOver(slug) {
    var groups = knownGroups();
    if (groups === undefined) return false;       // no list rendered — decide nothing
    var g = groups[slug];
    if (!g) return true;                          // not published any more = finished
    var until = stamp(g.until);
    if (!until) return false;                     // no window on it — leave it alone
    return nowStamp() > until;
  }

  // THE OTHER END OF THE WINDOW (Mike, 2026-09-28): "I don't want these fundraisers to
  // cascade out over a month of people placing orders. I want it to be a single week." A
  // principal's newsletter carries the link weeks early, and people click it and shop. So a
  // link clicked BEFORE shopping opens credits nobody and waives nothing: no cart tag, and
  // the $0 marker the share link drops in is taken back out. The family gets a bar with the
  // shopping dates and a calendar reminder instead. The slug IS remembered, quietly, so a
  // family who comes back on opening day through the plain store address still counts.
  // A date-only value opens at its first minute (the pickup lists' rule).
  //
  // EARLY_MIN (Mike 2026-09-28): "keep it advertised as five, but between us, let's make that
  // link work at four." Everything a family READS says the advertised time; the link starts
  // crediting this many minutes sooner. Equal to fr_early_min in spf-fulfillment-global.liquid
  // (the group's own pickup spot) and the hold script in spf-fundraiser-banner.liquid.
  //
  // SAME-WEEK EARLY ORDERS COUNT (Mike 2026-10-02, after two Mountview families ordered through
  // the link at noon and 3 PM on opening day and were credited to nobody): "If someone comes
  // there with the link and shops, even if it's early, it should just work for them." The
  // 09-28 rule above was about links shared WEEKS early; an order placed in the same ordering
  // week (Tuesday 00:00 -> Monday 23:59, the cycle the fundraiser's orders pack in) goes out
  // with the group's own week, so it now credits from that Tuesday. Earlier weeks still get
  // the reminder page. The hold script in spf-fundraiser-banner.liquid uses the same rule.
  var EARLY_MIN = 60;
  function notYetOpen(slug) {
    var groups = knownGroups();
    if (!groups || !groups[slug]) return false;   // unknown is windowOver's call, not ours
    var p = parts(groups[slug].from);
    if (!p) return false;
    var z = function (n) { return (n < 10 ? '0' : '') + n; };
    var num = function (t) {
      return parseInt('' + t.getUTCFullYear() + z(t.getUTCMonth() + 1) + z(t.getUTCDate()) + z(t.getUTCHours()) + z(t.getUTCMinutes()), 10);
    };
    var early = num(new Date(Date.UTC(p.y, p.mo - 1, p.d, p.h === null ? 0 : p.h, p.mi - EARLY_MIN)));
    // The Tuesday that starts the opening day's ordering week (Tuesday itself = 0 days back).
    var dow = new Date(Date.UTC(p.y, p.mo - 1, p.d)).getUTCDay();
    var week = num(new Date(Date.UTC(p.y, p.mo - 1, p.d - ((dow + 5) % 7), 0, 0)));
    return nowStamp() < Math.min(early, week);
  }

  // "2026-10-23T17:00" -> its parts, and a customer-readable "Friday, Oct 23 at 5 PM".
  // Built by hand, not toLocaleString, so every phone words it the same way.
  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function parts(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(String(s || ''));
    if (!m) return null;
    return { y: +m[1], mo: +m[2], d: +m[3], h: m[4] ? +m[4] : null, mi: m[5] ? +m[5] : 0 };
  }
  function dayLabel(p) {
    return DAYS[new Date(Date.UTC(p.y, p.mo - 1, p.d)).getUTCDay()] + ', ' + MONTHS[p.mo - 1] + ' ' + p.d;
  }
  function timeLabel(p) {
    if (p.h === null) return '';
    var h12 = p.h % 12 === 0 ? 12 : p.h % 12;
    return h12 + (p.mi ? ':' + (p.mi < 10 ? '0' : '') + p.mi : '') + (p.h < 12 ? ' AM' : ' PM');
  }

  // The shop's wall clock (Edmonton) -> a real instant, for the calendar file. Calendars
  // convert to the phone's own zone, so this must be exact; Intl is on every browser that
  // can run this page, and the fallback (the phone's zone) is right for nearly every family.
  var SHOP_ZONE = 'America/Edmonton';
  function wallMs(ms) {
    var f = new Intl.DateTimeFormat('en-US', { timeZone: SHOP_ZONE, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    var o = {};
    f.formatToParts(new Date(ms)).forEach(function (x) { o[x.type] = +x.value; });
    return Date.UTC(o.year, o.month - 1, o.day, o.hour % 24, o.minute);
  }
  function shopTimeToMs(p) {
    var want = Date.UTC(p.y, p.mo - 1, p.d, p.h === null ? 0 : p.h, p.mi);
    try {
      var guess = want;
      for (var i = 0; i < 2; i++) guess = want - (wallMs(guess) - guess);
      return guess;
    } catch (e) {
      return new Date(p.y, p.mo - 1, p.d, p.h === null ? 0 : p.h, p.mi).getTime();
    }
  }
  function icsStamp(ms) {
    return new Date(ms).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  }
  function icsText(s) {
    return String(s).replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
  }
  // RFC 5545 wants lines folded at 75 octets; counted in characters here, which is shorter
  // or equal for everything but the accented group names, and clients accept those.
  function icsFold(line) {
    var out = [];
    while (line.length > 74) { out.push(line.slice(0, 74)); line = ' ' + line.slice(74); }
    out.push(line);
    return out.join('\r\n');
  }

  function reminder(slug) {
    var g = knownGroups()[slug];
    var from = parts(g.from); var until = parts(g.until);
    var start = shopTimeToMs(from);
    var link = 'https://steelpony.ca/f/' + encodeURIComponent(slug);
    var title = g.label + ' fundraiser opens (Steel Pony Farm)';
    var details = 'Shopping runs ' + dayLabel(from) + ' to ' + dayLabel(until) + '. Shop with this link so 15% of your groceries goes to ' + g.label + ': ' + link;
    var ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Steel Pony Farm//Fundraiser//EN',
      'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', 'BEGIN:VEVENT',
      'UID:fundraiser-' + slug + '-' + icsStamp(start) + '@steelpony.ca',
      'DTSTAMP:' + icsStamp(Date.now()),
      'DTSTART:' + icsStamp(start),
      'DTEND:' + icsStamp(start + 30 * 60 * 1000),
      'SUMMARY:' + icsText(title),
      'DESCRIPTION:' + icsText(details),
      'URL:' + link,
      'BEGIN:VALARM', 'ACTION:DISPLAY', 'DESCRIPTION:' + icsText(title), 'TRIGGER:PT0M', 'END:VALARM',
      'END:VEVENT', 'END:VCALENDAR',
    ].map(icsFold).join('\r\n') + '\r\n';
    var google = 'https://calendar.google.com/calendar/render?action=TEMPLATE'
      + '&text=' + encodeURIComponent(title)
      + '&dates=' + icsStamp(start) + '/' + icsStamp(start + 30 * 60 * 1000)
      + '&details=' + encodeURIComponent(details);
    return { ics: ics, google: google, file: 'steel-pony-' + slug + '.ics' };
  }

  // iPhones open a text/calendar data: address straight into "Add to Calendar"; they do not
  // do that for a downloaded blob. Everything else takes the file as a download, which the
  // phone or computer then offers to its calendar.
  function openCalendar(r) {
    try {
      var ua = navigator.userAgent || '';
      var ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
      if (ios) {
        window.location.href = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(r.ics);
        return;
      }
      var url = URL.createObjectURL(new Blob([r.ics], { type: 'text/calendar;charset=utf-8' }));
      var a = document.createElement('a');
      a.href = url; a.download = r.file;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(function () { URL.revokeObjectURL(url); }, 10000);
    } catch (e) {
      window.open(r.google, '_blank', 'noopener');
    }
  }

  function placeBar(bar) {
    var hdr = null, els = document.querySelectorAll('header, .header, [class*="header"]');
    for (var i = 0; i < els.length; i++) {
      if (getComputedStyle(els[i]).position === 'sticky') { hdr = els[i]; break; }
    }
    if (hdr && hdr.parentNode) hdr.parentNode.insertBefore(bar, hdr.nextSibling);
    else document.body.insertBefore(bar, document.body.firstChild);
  }

  // The "not open yet" bar. Its own element, never the confirm bar: a family whose cart
  // already carries a LIVE group keeps that group's bar (the snippet hid it because the link
  // named another group; it is still true, so it is shown again).
  function paintPending(slug) {
    try {
      var g = knownGroups()[slug];
      var from = parts(g.from); var until = parts(g.until);
      if (!g.label || !from || !until) return;
      var live = document.querySelector('[data-spf-fr-bar]');
      if (live && live.style) live.style.visibility = '';
      var opens = dayLabel(from) + (timeLabel(from) ? ' at ' + timeLabel(from) : '');

      var bar = document.createElement('div');
      bar.className = 'spf-fr-bar';
      bar.setAttribute('role', 'status');
      bar.setAttribute('data-spf-fr-pending', slug);
      if (live) bar.style.position = 'relative';   // two sticky bars would sit on each other
      var a = document.createElement('span');
      a.appendChild(document.createTextNode('\uD83D\uDCC5 '));
      var b = document.createElement('strong');
      b.textContent = g.label;            // textContent, never innerHTML — the label is data
      a.appendChild(b);
      a.appendChild(document.createTextNode(' fundraiser shopping opens ' + opens + '.'));
      var c = document.createElement('span');
      c.className = 'pct';
      c.textContent = 'Shop ' + dayLabel(from) + ' to ' + dayLabel(until)
        + ' with this link and 15% of your groceries goes to them. Orders placed before then won\u2019t count.';
      var btn = document.createElement('button');
      btn.setAttribute('type', 'button');
      btn.textContent = 'Add a reminder to my calendar';
      var r = reminder(slug);
      btn.addEventListener('click', function () { openCalendar(r); });
      var gl = document.createElement('a');
      gl.setAttribute('href', r.google);
      gl.setAttribute('target', '_blank');
      gl.setAttribute('rel', 'noopener');
      gl.textContent = 'or Google Calendar';
      bar.appendChild(a); bar.appendChild(c); bar.appendChild(btn); bar.appendChild(gl);
      if (live && live.parentNode) live.parentNode.insertBefore(bar, live.nextSibling);
      else placeBar(bar);
    } catch (e) {
      /* silent — a missing bar must never break the page */
    }
  }

  // "Shop anyway" is remembered for this visit only, so the page does not come back on every
  // click through the store — but a fresh tap on the link next week shows it again.
  var ANYWAY = 'spf_fundraiser_anyway';
  function choseAnyway(slug) {
    try { return window.sessionStorage.getItem(ANYWAY) === slug; } catch (e) { return false; }
  }
  function rememberAnyway(slug) {
    try { window.sessionStorage.setItem(ANYWAY, slug); } catch (e) { /* no storage — bar still shows */ }
  }

  // Pickup or delivery is the regular run after the weekend: Wednesday and Thursday, two and
  // three days after shopping closes on the Monday.
  function plusDays(p, n) {
    var d = new Date(Date.UTC(p.y, p.mo - 1, p.d + n));
    return { y: d.getUTCFullYear(), mo: d.getUTCMonth() + 1, d: d.getUTCDate(), h: null, mi: 0 };
  }

  var COVER_CSS = [
    '.spf-fr-cover{position:fixed;inset:0;z-index:2147483000;overflow-y:auto;background:#f6f1e7;',
    'display:flex;align-items:center;justify-content:center;padding:24px 16px;box-sizing:border-box}',
    '.spf-fr-cover *{box-sizing:border-box}',
    '.spf-fr-card{width:100%;max-width:520px;background:#fff;border-radius:18px;padding:34px 28px 26px;',
    'text-align:center;box-shadow:0 10px 40px rgba(99,2,81,.12);border-top:6px solid #630251;color:#2b2b2b}',
    '.spf-fr-eyebrow{font-size:.8rem;letter-spacing:.12em;text-transform:uppercase;color:#630251;font-weight:700;margin:0 0 10px}',
    '.spf-fr-card h1{font-size:clamp(1.6rem,6vw,2.2rem);line-height:1.15;margin:0 0 8px;color:#1f1f1f}',
    '.spf-fr-opens{font-size:1.05rem;margin:0 0 22px;color:#444}',
    '.spf-fr-opens strong{color:#630251}',
    '.spf-fr-tiles{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:0 0 20px}',
    '.spf-fr-tile{background:#f6f1e7;border-radius:12px;padding:14px 10px}',
    '.spf-fr-tile b{display:block;font-size:.75rem;letter-spacing:.08em;text-transform:uppercase;color:#630251;margin-bottom:6px}',
    '.spf-fr-tile span{display:block;font-size:.98rem;line-height:1.35;font-weight:600}',
    '.spf-fr-note{font-size:.95rem;line-height:1.45;margin:0 0 22px;color:#444}',
    '.spf-fr-go{display:block;width:100%;border:0;border-radius:999px;background:#630251;color:#fff;',
    'font:inherit;font-weight:700;font-size:1.05rem;padding:15px 18px;cursor:pointer;margin:0 0 10px}',
    '.spf-fr-alt{display:block;width:100%;border:2px solid #630251;border-radius:999px;background:#fff;color:#630251;',
    'font:inherit;font-weight:700;font-size:.98rem;padding:12px 18px;cursor:pointer;text-decoration:none;margin:0 0 8px}',
    '.spf-fr-done{min-height:1.3em;font-size:.9rem;color:#2e6b34;margin:4px 0 14px}',
    '.spf-fr-or{display:flex;align-items:center;gap:10px;font-size:.8rem;color:#888;margin:2px 0 12px}',
    '.spf-fr-or:before,.spf-fr-or:after{content:"";flex:1;height:1px;background:#e6ddd0}',
    '.spf-fr-skip{display:block;width:100%;border:0;border-radius:999px;background:#f6f1e7;color:#630251;',
    'font:inherit;font-weight:600;font-size:.98rem;padding:12px 18px;cursor:pointer;margin:0 0 6px}',
    '.spf-fr-skip:hover{background:#efe6d6}',
    '.spf-fr-skipnote{font-size:.82rem;color:#777;margin:0}',
    '@media (max-width:420px){.spf-fr-tiles{grid-template-columns:1fr}.spf-fr-card{padding:28px 18px 20px}}',
    'html.spf-fr-covered,html.spf-fr-covered body{overflow:hidden}',
  ].join('');

  // THE FULL PAGE (Mike, 2026-09-28): a family who taps the school's link came FOR the
  // school, so before the weekend they meet the school, the dates and a reminder button —
  // not the store. A bar was the first cut; it is easy to miss, and a missed bar is an order
  // that silently does not count. "Shop now anyway" is there and honest about the cost.
  function showCover(slug) {
    try {
      var g = knownGroups()[slug];
      var from = parts(g.from); var until = parts(g.until);
      if (!g.label || !from || !until) return false;
      var r = reminder(slug);
      var opens = dayLabel(from) + (timeLabel(from) ? ' at ' + timeLabel(from) : '');
      var shortDate = MONTHS[from.mo - 1] + ' ' + from.d;

      var css = document.createElement('style');
      css.textContent = COVER_CSS;
      var cover = document.createElement('div');
      cover.className = 'spf-fr-cover';
      cover.setAttribute('role', 'dialog');
      cover.setAttribute('aria-modal', 'true');
      cover.setAttribute('aria-label', g.label + ' fundraiser');
      cover.setAttribute('data-spf-fr-cover', slug);
      cover.appendChild(css);

      var card = document.createElement('div');
      card.className = 'spf-fr-card';
      var add = function (tag, cls, text) {
        var n = document.createElement(tag);
        if (cls) n.className = cls;
        if (text != null) n.textContent = text;   // textContent, never innerHTML — the label is data
        card.appendChild(n);
        return n;
      };
      add('p', 'spf-fr-eyebrow', 'Steel Pony Farm fundraiser');
      add('h1', '', g.label);
      var o = add('p', 'spf-fr-opens', 'Shopping opens ');
      var os = document.createElement('strong'); os.textContent = opens; o.appendChild(os);

      var tiles = add('div', 'spf-fr-tiles');
      var tile = function (head, body) {
        var t = document.createElement('div'); t.className = 'spf-fr-tile';
        var h = document.createElement('b'); h.textContent = head;
        var s = document.createElement('span'); s.textContent = body;
        t.appendChild(h); t.appendChild(s); tiles.appendChild(t);
      };
      tile('Shop', dayLabel(from) + ' to ' + dayLabel(until));
      tile('Pickup or delivery', dayLabel(plusDays(until, 2)) + ' or ' + dayLabel(plusDays(until, 3)));

      add('p', 'spf-fr-note', '15% of the groceries in every order placed that weekend goes to ' + g.label
        + '. Orders placed before then won’t count for the fundraiser.');

      var go = add('button', 'spf-fr-go', 'Remind me on ' + shortDate);
      go.setAttribute('type', 'button');
      var gl = add('a', 'spf-fr-alt', 'Add to Google Calendar');
      gl.setAttribute('href', r.google);
      gl.setAttribute('target', '_blank');
      gl.setAttribute('rel', 'noopener');
      var done = add('p', 'spf-fr-done', '');
      var said = function () {
        done.textContent = 'Saved? You’re all set. See you ' + shortDate + '.';
      };
      go.addEventListener('click', function () { openCalendar(r); said(); });
      gl.addEventListener('click', said);

      // Mike 2026-09-28: quieter than the reminder, but a real button, not fine print — a
      // family who shops now is still welcome, they just need to know it isn't the fundraiser.
      add('p', 'spf-fr-or', 'or');
      var skip = add('button', 'spf-fr-skip', 'Shop the store now');
      add('p', 'spf-fr-skipnote', 'Orders placed before ' + shortDate + ' won’t count toward the fundraiser.');
      skip.setAttribute('type', 'button');
      skip.addEventListener('click', function () {
        rememberAnyway(slug);
        if (cover.parentNode) cover.parentNode.removeChild(cover);
        document.documentElement.classList && document.documentElement.classList.remove('spf-fr-covered');
        paintPending(slug);
      });

      cover.appendChild(card);
      document.body.appendChild(cover);
      if (document.documentElement.classList) document.documentElement.classList.add('spf-fr-covered');
      if (go.focus) go.focus();
      return true;
    } catch (e) {
      return false;   // no page — the caller falls back to the bar
    }
  }

  // An early link: the full page, unless this visit already chose to shop anyway (then the
  // bar), or the page could not be built (then the bar too — never nothing).
  function earlyNotice(slug) {
    if (choseAnyway(slug) || !showCover(slug)) paintPending(slug);
  }

  // The banner snippet hides the store before first paint when the link is an early one, so
  // the products do not flash under the page. Released here once the page (or bar) is up;
  // the snippet also releases it on its own after a few seconds in case this file never runs.
  function releaseHold() {
    try { document.documentElement.classList.remove('spf-fr-hold'); } catch (e) {}
  }

  // Clears ONE group's tag, never whatever happens to be there. An old link for a finished
  // group, clicked by a family whose cart already carries a LIVE group, used to wipe the live
  // group's tag and marker — the one crossover that loses a school its money (2026-09-23).
  // keepStored: a not-yet-open group's slug stays remembered for its opening day.
  function clearAttribution(slug, keepStored) {
    if (!keepStored && storedSlug() === slug) clearStored();
    if (document.documentElement.getAttribute('data-spf-fundraiser') === slug) {
      document.documentElement.removeAttribute('data-spf-fundraiser');
    }
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return null;
        var current = (cart.attributes && cart.attributes[ATTR]) || '';
        // Another group's tag: not ours to touch, and its marker is still earning its keep.
        if (current && current !== slug) return null;
        var jobs = [];
        if (current) {
          var attributes = {};
          attributes[ATTR] = '';
          jobs.push(fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: attributes }),
          }));
        }
        // The $0 marker is the other half of the tag: left behind it shows as a mystery
        // line and, while some other fundraiser is running, would carry the $50 exemption.
        var handle = markerHandle();
        var items = (cart && cart.items) || [];
        for (var i = 0; handle && i < items.length; i++) {
          if (items[i].handle === handle) {
            var updates = {};
            updates[items[i].key] = 0;
            jobs.push(fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates: updates }),
            }));
            break;
          }
        }
        // The badge and cart gate still count the removed marker until told (seen on the
        // preview 2026-09-28: the icon read 3 over a 2-item cart).
        return Promise.all(jobs).then(function () { if (jobs.length) announceCart(); });
      })
      .catch(function () { /* silent — never block shopping */ });
  }

  function paintBar(slug) {
    try {
      var label = labelFor(slug);
      if (!label) return;
      var existing = document.querySelector('[data-spf-fr-bar]');
      // A bar already saying the right thing: leave it alone. Anything else is repainted:
      // - a RECOVER bar ("Tap here so your order counts") on a page whose cart already
      //   carries the tag tells a family to do something to be counted when they already are
      //   (Mike, 2026-09-22: "they should not need to tap to make their order count");
      // - a CONFIRM bar naming a DIFFERENT group. The server paints from the cart's old tag
      //   before this file writes the new one, so a family arriving on Wonderflow's link with
      //   a cart still tagged St. Gregory was told "You're supporting St Gregory" (Mike,
      //   2026-09-23). The snippet hides that bar before paint ('stale'); this fills it in.
      if (existing && existing.getAttribute('data-spf-fr-mode') === 'confirm') {
        var shown = existing.getAttribute('data-spf-fr-slug');
        var strong = existing.querySelector && existing.querySelector('strong');
        if (shown ? shown === slug : (strong && strong.textContent === label)) {
          existing.style.visibility = '';   // the snippet may have hidden it; it is right
          return;
        }
      }
      var bar = existing || document.createElement('div');
      bar.className = 'spf-fr-bar';
      bar.setAttribute('role', 'status');
      bar.setAttribute('data-spf-fr-bar', '');
      bar.setAttribute('data-spf-fr-mode', 'confirm');
      bar.setAttribute('data-spf-fr-slug', slug);
      if (bar.style) bar.style.visibility = '';
      while (bar.firstChild) bar.removeChild(bar.firstChild);
      var a = document.createElement('span');
      a.appendChild(document.createTextNode("\uD83D\uDC9A You're supporting "));
      var b = document.createElement('strong');
      b.textContent = label;              // textContent, never innerHTML — the label is data
      a.appendChild(b);
      var c = document.createElement('span');
      c.className = 'pct';
      c.textContent = '\u2014 15% of your groceries goes to them';
      bar.appendChild(a); bar.appendChild(c);
      var n = familiesFor(slug);
      if (n >= COUNT_MIN) {
        var d = document.createElement('span');
        d.className = 'pct';
        d.textContent = '\u00B7 ' + n + ' families have ordered so far';
        bar.appendChild(d);
      }
      if (existing) return;                 // upgraded in place — already in the page
      placeBar(bar);
    } catch (e) {
      /* silent — a missing bar must never break the page */
    }
  }

  // A group's own pickup spot is offered only to that group's families (Mike, 2026-09-23:
  // "only visible to people who visit with the link"). The server renders it hidden unless
  // the cart already carries the tag, and on the pageview a share link lands on it does not
  // yet. So show this group's spot, and hide any other group's the page was rendered with.
  function showSpots(slug) {
    try {
      var spots = document.querySelectorAll('[data-spf-fr-spot]');
      for (var i = 0; i < spots.length; i++) {
        spots[i].hidden = spots[i].getAttribute('data-spf-fr-spot') !== slug;
      }
    } catch (e) { /* silent: the regular spots are untouched either way */ }
  }

  function init() {
    var fromLink = paramSlug();

    // Expiry first, and it applies to a link too: a shared link that outlives its window
    // must not re-attribute anybody. Each source is judged on its own (2026-09-23): an
    // expired LINK must not take down a live group the family already carries, and an
    // expired tag left on the CART (storage cleared, cookie not) must not ride to an order.
    if (fromLink && windowOver(fromLink)) {
      clearAttribution(fromLink);
      fromLink = null;
    }
    // Too EARLY is the other refusal (2026-09-28): the link is remembered, nothing else.
    // Its marker comes back out of the cart, unless the cart belongs to another live group.
    var pending = null;
    if (fromLink && notYetOpen(fromLink)) {
      pending = fromLink;
      clearAttribution(pending, true);
      fromLink = null;
    }
    var stored = fromLink ? null : storedSlug();
    if (stored && windowOver(stored)) {
      clearAttribution(stored);
      stored = null;
    }
    var onCart = cartSlug();
    if (onCart && onCart !== fromLink && onCart !== stored && windowOver(onCart)) {
      clearAttribution(onCart);
      onCart = null;
    }

    // Without a link, the CART is the latest explicit choice and storage never overrides it
    // (applyToCart below refuses to) — so the bar and storage follow the cart too, or the bar
    // would name the stored group while the order carries the other one.
    if (!fromLink && onCart && onCart !== stored) {
      stored = onCart;
      writeStored(onCart);
    }

    if (fromLink) writeStored(fromLink);

    var slug = fromLink || stored;
    // A remembered group that has not opened yet waits, uncredited, for its first minute.
    var waiting = slug && notYetOpen(slug) ? slug : null;
    if (waiting) slug = null;
    // A slug remembered from an EARLY tap, for a signed-in customer who has ordered before:
    // never credited (see REPEAT). Storage is kept, so tapping the link again in the week
    // still counts; a cart tag this same early tap put there is taken back off.
    if (slug && !fromLink && slug === storedSlug() && storedEarly() && isRepeat()) {
      if (cartSlug() === slug) clearAttribution(slug, true);
      slug = null;
    }
    // Remember an early link only when there is no live group to lose by it.
    if (pending && !slug) writeStored(pending, true);
    if (!slug) {
      // No live group behind this cart, so a $0 marker in it is an orphan (2026-10-01): it
      // got there by reordering an old fundraiser order (Shopify's "Buy again", or the
      // regulars page before its fix). The cart hides the line, so the shopper cannot see or
      // remove it, and while any other group is running it waives the $50 minimum. Take it
      // out. '' matches no cart tag, so only the marker goes; storage is left alone.
      clearAttribution('', true);
      // The snippet hid the bar for a link we then refused (over, or not a group we know).
      // The cart's own tag was left as it was, so its bar is true again: show it.
      var hidden = document.querySelector('[data-spf-fr-bar]');
      if (hidden && hidden.style) hidden.style.visibility = '';
      if (pending) earlyNotice(pending);
      else if (waiting && choseAnyway(waiting)) paintPending(waiting);
      releaseHold();
      return;
    }

    document.documentElement.setAttribute('data-spf-fundraiser', slug);
    paintBar(slug);
    if (pending) earlyNotice(pending);
    releaseHold();
    showSpots(slug);
    applyToCart(slug, !!fromLink);
    ensureMarker();
    // The cart is usually EMPTY on the load that matters (they arrive, then shop), so a
    // one-shot check at init would miss every returning participant. Re-check when the
    // cart changes. Guarded against its own add re-triggering it: ensureMarker returns
    // early once the marker is present, so the second pass is a single /cart.js read.
    document.addEventListener('cart:update', ensureMarker);
    document.addEventListener('cart:refresh', ensureMarker);
    window.addEventListener('pageshow', ensureMarker);
    window.addEventListener('focus', ensureMarker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
